import { useRef, useState, useEffect } from 'react';
import { Typography, Grid } from '@mui/material';
import IngredientsCard from './IngredientsCard';
import { showCaseIngredients } from './constants';
import DoubleArrowButtons from '../../../commons/components/ui/DoubleArrowButtons/DoubleArrowButtons';
import { useResponsive } from '../../../commons/hooks';
import styles from './Ingredients.module.scss';

export default function Ingredients({ productType, id = '' }) {
  const showcaseRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);
  const productIngredientsData = showCaseIngredients[productType];

  // Responsive breakpoints
  const isMobile = useResponsive('down', 'sm');
  const isTablet = useResponsive('between', 'sm', 'md');

  // Update scroll state based on container and card dimensions
  const updateScrollState = () => {
    if (!showcaseRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = showcaseRef.current;
    const firstCard = showcaseRef.current.querySelector(
      `.${styles.ingredientContainer} > div`
    );
    const newCardWidth = firstCard ? firstCard.offsetWidth : 0;
    const maxScroll = scrollWidth - clientWidth;

    const newCanScrollPrev = scrollLeft > 0;
    const newCanScrollNext = maxScroll > 0 && scrollLeft < maxScroll - 1;

    setCardWidth(newCardWidth);
    setCanScrollPrev(newCanScrollPrev);
    setCanScrollNext(newCanScrollNext);
  };

  // Scroll handlers
  const scrollPrev = () => {
    if (showcaseRef.current && cardWidth > 0) {
      showcaseRef.current.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    }
  };

  const scrollNext = () => {
    if (showcaseRef.current && cardWidth > 0) {
      showcaseRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  // Set up listeners
  useEffect(() => {
    if (!showcaseRef.current) return;

    updateScrollState(); // Initial state

    const handleScroll = () => updateScrollState();
    const handleResize = () => updateScrollState();

    showcaseRef.current.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      showcaseRef.current?.removeEventListener('scroll', handleScroll);
      window?.removeEventListener('resize', handleResize);
    };
  }, [productIngredientsData]);

  // Determine if buttons should be hidden (no scrolling needed)
  const shouldHideButtons = !canScrollPrev && !canScrollNext;

  return (
    <div className={styles.ingredientShowcaseExtender} id={id}>
      {/* Header and Buttons inside ingredientShowcaseExtender */}
      <div className={styles.headerRow}>
        <div className={styles.headerContent}>
          <Typography
            variant={'h5'}
            component="span"
            color="primary.black"
            className={styles.headerText}
          >
            Our Ingredients **
          </Typography>
          <div className={styles.headingWrap}>
            <Typography
              variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h1'}
              component="span"
              color="primary.light"
              sx={{ textWrap: 'pretty', display: 'inline' }}
            >
              {productIngredientsData.title}{' '}
              <Typography
                variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h1'}
                component="span"
                color="primary.main"
                sx={{ textWrap: 'pretty', display: 'inline' }}
              >
                {productIngredientsData.title2}{' '}
              </Typography>
            </Typography>
            <Typography
              variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h1'}
              component="span"
              color="primary.light"
              className={styles.noWrap}
            >
              {productIngredientsData.title3}
            </Typography>
          </div>
        </div>
        {!shouldHideButtons && (
          <div className={styles.buttonContainer}>
            <DoubleArrowButtons
              prevBtnHandler={scrollPrev}
              nextBtnHandler={scrollNext}
              prevDisabled={!canScrollPrev}
              nextDisabled={!canScrollNext}
            />
          </div>
        )}
      </div>

      {/* Ingredients Grid */}
      <div className={styles.ingredientShowcaseContainer}>
        <Grid
          container
          wrap="nowrap"
          className={`${styles.ingredientContainer} ${
            shouldHideButtons ? styles.alignCenter : ''
          }`}
          ref={showcaseRef}
          sx={{
            'overflowX': 'auto',
            'scrollSnapType': 'x mandatory',
            'WebkitOverflowScrolling': 'touch',
            'scrollbarWidth': 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            'marginTop': '40px', // Spacing after header
          }}
        >
          {productIngredientsData.content.map((item, index) => (
            <Grid
              item
              key={index}
              xl={3}
              sx={{
                scrollSnapAlign: index === 0 ? 'start' : 'center',
                ...(index === 0 && { className: styles.leftPadding }),
                ...(index === productIngredientsData.content.length - 1 && {
                  className: styles.rightPadding,
                }),
              }}
            >
              <IngredientsCard item={item} />
            </Grid>
          ))}
        </Grid>
      </div>
    </div>
  );
}
